import { CollectionPreferencesProps } from '@amzn/awsui-components-console';
import {
  defaultTablePreferences,
  loadTablePreferences,
  normaliseTablePreferences,
  pageSizeOf,
  sanitiseContentDisplay as sanitiseAgainstConfig,
  saveTablePreferences,
  type TablePreferencesConfig,
  type TablePreferencesValue,
} from 'src/common/ui/TablePreferences';
import { SORT_FIELD_LABEL_IDS } from '../sortState';
/*
 * TYPE-ONLY import, and it must stay that way: a value import from `SearchPage`
 * closes a require cycle (`SearchPage` → `SearchResultsTable` → here) and leaves
 * this module's exports undefined at eval time. Hence the literal mode keys
 * below, still checked against `SearchMode` by the `Record`.
 */
import type { SearchMode } from '../SearchPage';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

/**
 * The search table's column sets and its binding to the shared core; only what is
 * search-specific lives here — one column set and one key per mode.
 */

/**
 * Offered page sizes, and the allowlist for values read back from storage. Search-owned,
 * not the shared default: only search's size drives a backend query bounded by
 * `MAX_PAGE_SIZE`, pinned by a test. See `searchTablePreferences.test.ts`.
 */
export const PAGE_SIZE_OPTIONS: readonly number[] = [25, 50, 75];

export const DEFAULT_PAGE_SIZE = 25;

/**
 * The trailing Details/actions column, in both modes. It is `alwaysVisible` in
 * the options below and pinned last by the config, because the SCSS width pin
 * targets whichever header is rightmost (`thead th[data-rightmost]`) — hiding it
 * or moving it would squash whatever column ended up there instead.
 */
const ACTIONS_COLUMN_ID = 'actions';

/** Resource id for the label the actions column shows in the preferences list; its header is blank. */
const ACTIONS_COLUMN_LABEL_ID = 'search-results-details';

/**
 * Column options per mode; the order is the default column order and mirrors the
 * column-definition arrays in `SearchResultsTable`. The two modes' ids only
 * partly overlap, which is why they persist under separate keys and are
 * reconciled against the options of the mode being read.
 */
const columnOptions = (
  t: TranslateFn,
): Record<
  SearchMode,
  readonly CollectionPreferencesProps.ContentDisplayOption[]
> => ({
  SOURCE: [
    { id: 'stringId', label: t(SORT_FIELD_LABEL_IDS.STRING_ID) },
    { id: 'group', label: t(SORT_FIELD_LABEL_IDS.STRING_GROUP) },
    { id: 'namespace', label: t(SORT_FIELD_LABEL_IDS.NAMESPACE) },
    { id: 'sourceContent', label: t('search-column-source-text') },
    { id: 'status', label: t(SORT_FIELD_LABEL_IDS.STATUS) },
    {
      id: ACTIONS_COLUMN_ID,
      label: t(ACTIONS_COLUMN_LABEL_ID),
      alwaysVisible: true,
    },
  ],
  TRANSLATION: [
    { id: 'stringId', label: t(SORT_FIELD_LABEL_IDS.STRING_ID) },
    { id: 'group', label: t(SORT_FIELD_LABEL_IDS.STRING_GROUP) },
    { id: 'namespace', label: t(SORT_FIELD_LABEL_IDS.NAMESPACE) },
    { id: 'localizedText', label: t('search-column-localized-text') },
    { id: 'locale', label: t('search-column-locale') },
    {
      id: ACTIONS_COLUMN_ID,
      label: t(ACTIONS_COLUMN_LABEL_ID),
      alwaysVisible: true,
    },
  ],
});

/** The shared config for a mode. The key is verbatim: changing it discards preferences. */
export function searchTablePreferencesConfig(
  t: TranslateFn,
  mode: SearchMode,
): TablePreferencesConfig {
  return {
    storageKey: `LangHub-searchTablePreferences-${mode}`,
    columnOptions: columnOptions(t)[mode],
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    pageSizeLabel: (t, pageSize) =>
      t('search-page-size-results', { value: pageSize }),
    pinnedLastColumnId: ACTIONS_COLUMN_ID,
  };
}

/** The preferences list for a mode, in default column order. */
export function columnOptionsForMode(
  t: TranslateFn,
  mode: SearchMode,
): readonly CollectionPreferencesProps.ContentDisplayOption[] {
  return columnOptions(t)[mode];
}

/** `pageSize` is required where the shared type leaves it optional: search always pages. */
export interface SearchTablePreferences extends Omit<
  TablePreferencesValue,
  'pageSize'
> {
  readonly pageSize: number;
}

/** The defaults, and the fallback for anything unreadable in storage. */
export function defaultSearchTablePreferences(
  t: TranslateFn,
  mode: SearchMode,
): SearchTablePreferences {
  return withRequiredPageSize(
    defaultTablePreferences(searchTablePreferencesConfig(t, mode)),
  );
}

/** Narrows the optional `pageSize`; the fallback is a type guard, not behaviour. */
function withRequiredPageSize(
  value: TablePreferencesValue,
): SearchTablePreferences {
  return { ...value, pageSize: pageSizeOf(value, DEFAULT_PAGE_SIZE) };
}

/** See the shared `sanitiseContentDisplay`; this binds it to a mode. */
export function sanitiseContentDisplay(
  t: TranslateFn,
  stored: unknown,
  mode: SearchMode,
): CollectionPreferencesProps.ContentDisplayColumn[] {
  return sanitiseAgainstConfig(stored, searchTablePreferencesConfig(t, mode));
}

/** Normalises a Cloudscape confirm payload for a mode. */
export function normaliseSearchTablePreferences(
  t: TranslateFn,
  preferences: CollectionPreferencesProps.Preferences,
  mode: SearchMode,
): SearchTablePreferences {
  return withRequiredPageSize(
    normaliseTablePreferences(
      preferences,
      searchTablePreferencesConfig(t, mode),
    ),
  );
}

/** Reads a mode's persisted preferences, falling back to the defaults. */
export function loadSearchTablePreferences(
  t: TranslateFn,
  mode: SearchMode,
): SearchTablePreferences {
  return withRequiredPageSize(
    loadTablePreferences(searchTablePreferencesConfig(t, mode)),
  );
}

/** Persists a mode's preferences. */
export function saveSearchTablePreferences(
  t: TranslateFn,
  mode: SearchMode,
  preferences: SearchTablePreferences,
): void {
  saveTablePreferences(searchTablePreferencesConfig(t, mode), preferences);
}

export { columnDisplayDndLabels } from 'src/common/ui/TablePreferences';
